// Angular
import { Component, OnInit, PLATFORM_ID, inject, DOCUMENT, ChangeDetectionStrategy, signal, DestroyRef, OnDestroy } from '@angular/core';
import { isPlatformBrowser, AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
// Material
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltip } from '@angular/material/tooltip';
// Constant
import { APP_CONSTANT, CART_PART_LS_KEY, ORIGIN_QUICK_CART } from '@constants';
import { environment } from '@environment';
// Utils
import { CartService, CommonService, GlobalErrorHandler } from '@core/utils';
import { PartSearchService } from '../../../partsearch-shared-module/service';
// Transloco
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
// Material
import { MatSnackBar } from '@angular/material/snack-bar';
// RxJS
import { catchError, debounceTime, distinctUntilChanged, filter, Observable } from 'rxjs';
import { get, includes, map, toLower, trim } from 'lodash';
// Service
import { CookieService } from 'ngx-cookie-service';
import { LayoutService } from '../../services';
// Component
import { CartExistDialogComponent } from '../../existing-carts/cart-exist-dialog/cart-exist-dialog.component';
import { ExistingCartsComponent } from '../../existing-carts/existing-carts.component';
// Modules
import { InlineSVGModule } from 'ng-inline-svg-2';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
// Pipes
import { CompanyRatingPipe, GaSelectItemDirective, imageBgURLPipe, OrderHandlingTimePipe, PDPLURLPipe } from '@app/core/_base/layout';

type ResellerGroup = {
  companyId: number;
  companyName: string;
  items: any[];
};

@Component({
  selector: 'app-quick-cart-view',
  templateUrl: './quick-cart-view.component.html',
  styleUrls: ['./quick-cart-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective, RouterLink, InlineSVGModule, FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatError, ExistingCartsComponent, AsyncPipe, GaSelectItemDirective, PDPLURLPipe, CompanyRatingPipe, imageBgURLPipe, OrderHandlingTimePipe, NgTemplateOutlet, MatTabsModule, MatTooltip]
})
export class QuickCartViewComponent implements OnInit, OnDestroy {

  // Private
  private readonly translocoService = inject(TranslocoService);
  private readonly partSearchService = inject(PartSearchService);
  private readonly globalErrorHandler = inject(GlobalErrorHandler);
  private readonly snackBar = inject(MatSnackBar);
  private readonly cookieService = inject(CookieService);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  readonly activeOffcanvas = inject(NgbActiveOffcanvas);
  private readonly dialog = inject(MatDialog);
  private readonly commonService = inject(CommonService);
  private readonly layoutService = inject(LayoutService);
  private readonly cartService = inject(CartService);
  private platformId = inject<Object>(PLATFORM_ID);
  private document = inject<Document>(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  // Public
  env = environment;
  isloading = signal<boolean>(true);
  languageChange$: Observable<string>;
  products: Array<any> = [];
  resellerProducts: Array<{ companyId: number; companyName: string; items: any[] }> = [];
  isCartEmpty = signal<boolean>(false);
  loadingOnNavigateToCheckout: boolean = false;
  cartSearchForm: FormGroup;
  checkoutForms = new Map<string, FormGroup>();
  isSearching = signal<boolean>(false);
  previousCarts: Array<any> = [];
  checkoutUrl: string = environment.checkoutUrl;
  customerEmail: any;

  /**
   * On Init
   * @memberof QuickCartViewComponent
   */
  ngOnInit(): void {

    // Init Search cart form by email or cart id
    this.initCartSearchForm();

    // Get Current live language
    this.languageChange$ = this.translocoService.langChanges$;

    // Get cart parts count
    const parts = this.commonService.getFromLocalStorage(CART_PART_LS_KEY);
    if (parts) {
      this.isCartEmpty.set(parts.length > 0 ? false : true);
      if (this.isCartEmpty()) {
        this.cartService.updateCartParts(new Set<number>());
        return;
      };
      this.partSearchService.getSavedPartsProductList(parts, ORIGIN_QUICK_CART).pipe(
        catchError(error => this.globalErrorHandler.handleError('on-get-quick-cart-parts', error)),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe((response: any) => {
        if (response?.status === 422) {
          const lang = this.translocoService.getActiveLang();
          this.router.navigate(['/', lang, 'captcha'], {
            replaceUrl: true,
            queryParams: {
              timestamp: new Date().getTime(),
              returnUrl: `/${lang}`
            }
          });
          return;
        }
        this.isloading.set(false);
        const { products, resellerGroups } = this.splitCartItemsByReseller(get(response, 'data.result', []));
        this.products = products;
        this.resellerProducts = resellerGroups;
        if (response?.success) this.handleCartProducts(response);
        // Send Google Analytics Data
        if (environment.googleAnalytics.isEnabled && isPlatformBrowser(this.platformId) && this.products.length > 0) this.sendViewCartAndBeginCheckoutEvent('view_cart', this.products);
      });
    } else {
      this.isCartEmpty.set(true);
      this.cartService.updateCartParts(new Set<number>([]))
    }

    // Close quick panel if route changed
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((event: any) => {
      this.activeOffcanvas.close();
    });

  }

  /**
   * OnDestroy
   * @memberof QuickCartViewComponent
   */
  ngOnDestroy(): void {
    this.loadingOnNavigateToCheckout = false;
  }

  /**
   * On Delete Cart Item
   * @param {number} index
   * @param {number} itemId
   * @param {*} analytics
   * @memberof QuickCartViewComponent
   */
  onDeleteItem(itemId: number, analytics: any): void {
    // 1) Remove from local arrays
    const removed = this.removeItemLocally(itemId);
    if (!removed) return;

    // 2) Update local storage / service
    this.cartService.removeFromCart(itemId);

    // 3) Update empty state
    const totalCount = this.products.length + this.resellerProducts.reduce((sum, g) => sum + g.items.length, 0);
    this.isCartEmpty.set(totalCount === 0);

    // 4) Toast
    this.snackBar.open(this.translocoService.translate('sentence_part_removed_from_cart'), this.translocoService.translate('label_close'), { panelClass: ['success-dialog'] });

    // 5) GA
    if (environment.googleAnalytics.isEnabled && isPlatformBrowser(this.platformId)) {
      this.sendDeleteGAEvent(analytics);
    }
  }

  /**
   * On Navigate to checkout
   * @memberof QuickCartViewComponent
   */
  onNavigateToCheckout(items: any, companyid: number | null, source: string, form: FormGroup): void {
    if (form.invalid) { return; }
    this.loadingOnNavigateToCheckout = true;
    const email = trim(form.controls.email.value).toLowerCase();
    const postIds = items.map((i: any) => i.id);
    const payload = { q: email, post_id: postIds };
    this.partSearchService.getCartByEmailOrCartId(payload).pipe(
      catchError(error => this.globalErrorHandler.handleError('on-cart-exists-check', error)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((response: any) => {
      if (response.success && response.total > 0) {
        this.dialog.open(CartExistDialogComponent, {
          data: {
            previousCarts: response?.data,
            customerEmail: email
          },
          panelClass: ['w-450px', 'w-md-600px']
        });
        this.loadingOnNavigateToCheckout = false
      } else {
        const jwt = this.commonService.getFromLocalStorage(APP_CONSTANT.JWT_TOKEN_KEY);
        this.handleCheckout(jwt, email, items, companyid);
      }
    });
  }

  /**
   * On Search Cart from email or cart id
   * @memberof QuickCartViewComponent
   */
  onSearch(): void {
    if (this.cartSearchForm.invalid) { return; }
    this.isSearching.set(true);
    this.previousCarts = [];
    const searchText = this.cartSearchForm.controls.searchText.value;
    const inputText = toLower(trim(searchText));
    if (inputText === '') return;
    const isEmail = includes(searchText, '@');
    this.partSearchService.getCartByEmailOrCartId({ q: trim(inputText).toLowerCase() }).pipe(
      catchError(error => this.globalErrorHandler.handleError('on-get-cart-by-email-address', error)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((response: any) => {
      this.isSearching.set(false);
      this.previousCarts = get(response, 'data', []);
      this.customerEmail = isEmail ? inputText : undefined;
      if (this.previousCarts.length === 0) {
        this.snackBar.open(this.translocoService.translate('sentence_no_cart_found'), this.translocoService.translate('label_close'), {
          panelClass: ['danger-dialog']
        });
      }
    });
  }

  /**
   * Get checkout form
   * @param {string} source
   * @param {(number | null)} companyId
   * @return {*}  {FormGroup}
   * @memberof QuickCartViewComponent
   */
  getCheckoutForm(source: string, companyId: number | null): FormGroup {
    const key = companyId == null ? `${source}:_` : `${source}:${companyId}`;

    if (!this.checkoutForms.has(key)) {
      const user = this.commonService.getFromLocalStorage(APP_CONSTANT.LOGGED_USER);
      const initialEmail = get(user, 'user.email', '');

      const formGroup = this.fb.group({
        email: [initialEmail, [Validators.required, Validators.email]],
      });

      // syncing email across multiple FormGroups
      formGroup.get('email')!.valueChanges.pipe(
        debounceTime(250),
        distinctUntilChanged(),
        filter(() => formGroup.get('email')!.valid),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((value) => {
        for (const [otherKey, otherForm] of this.checkoutForms.entries()) {
          if (otherKey === key) continue;
          otherForm.get('email')!.setValue(value, { emitEvent: false });
        }
      });
      this.checkoutForms.set(key, formGroup);
    }
    return this.checkoutForms.get(key)!;
  }

  /**
   * Split cart items into normal and reseller cart items
   * @private
   * @param {any[]} items
   * @return {*} 
   * @memberof QuickCartViewComponent
   */
  private splitCartItemsByReseller(items: any[]) {
    if (!Array.isArray(items) || items.length === 0) return { products: [], resellerGroups: [] as Array<{ companyId: number; companyName: string; items: any[] }> };

    // Gather all reseller company IDs present in the cart
    const resellerIds = new Set<number>((items ?? []).map(i => get(i, 'reseller.company.id')).filter((id: unknown): id is number => Number.isInteger(id)));

    // Use a Map to build grouped structure efficiently
    const groupMap = new Map<number, { companyId: number; companyName: string; items: any[] }>();
    const products: any[] = [];

    for (const item of items) {
      const rid = get(item, 'reseller.company.id') as number | undefined;
      const cid = get(item, 'company.id') as number | undefined;

      // Determine grouping target: reseller first, else promote if company.id is a known resellerId
      let targetId: number | undefined;
      let targetName: string | undefined;

      if (Number.isInteger(rid)) {
        targetId = rid!;
        targetName = get(item, 'reseller.company.name');
      } else if (Number.isInteger(cid) && resellerIds.has(cid!)) {
        targetId = cid!;
        // If promoted, use the *company* name on the item (fallback to reseller.company.name if present)
        targetName = get(item, 'company.name') ?? get(item, 'reseller.company.name');
      }

      if (Number.isInteger(targetId)) {
        if (!groupMap.has(targetId!)) {
          groupMap.set(targetId!, {
            companyId: targetId!,
            companyName: targetName ?? 'Unknown seller',
            items: [],
          });
        }
        groupMap.get(targetId!)!.items.push(item);
      } else {
        products.push(item); // stays as normal product
      }
    }

    // Convert map to array (optionally sort by companyName)
    const resellerGroups = Array.from(groupMap.values()).sort((a, b) => String(a.companyName).localeCompare(String(b.companyName)));

    return { products, resellerGroups };
  }

  /**
   * Removes a product from either the main products list or from one of the reseller groups.
   * @private
   * @param {number} itemId - The unique identifier of the product to be removed.
   * @returns {boolean} Returns `true` if the item was found and removed successfully; otherwise, `false`.
   */
  private removeItemLocally(itemId: number): boolean {
    // Attempt to remove from the main products list
    const productIndex = this.products.findIndex(product => product?.id === itemId);
    if (productIndex > -1) {
      this.products.splice(productIndex, 1);
      this.products = [...this.products];
      return true;
    }

    // Attempt to remove from reseller groups
    for (let groupIndex = 0; groupIndex < this.resellerProducts.length; groupIndex++) {
      const resellerGroup = this.resellerProducts[groupIndex];
      const itemIndexInGroup = resellerGroup.items.findIndex(product => product?.id === itemId);

      if (itemIndexInGroup > -1) {
        resellerGroup.items.splice(itemIndexInGroup, 1);

        // If the reseller group has no items left, remove the group entirely
        if (resellerGroup.items.length === 0) {
          this.resellerProducts.splice(groupIndex, 1);
        }

        this.resellerProducts = this.resellerProducts.map(group => ({
          ...group,
          items: [...group.items]
        }));

        return true;
      }
    }
    return false;
  }

  /**
   * Init Search cart by email or cart-id form
   * @private
   * @memberof QuickCartViewComponent
   */
  private initCartSearchForm() {
    this.cartSearchForm = this.fb.group({
      searchText: ['', Validators.required],
    })
  }


  /**
   * Get Site Subdomain for cookies so cookie works.
   * @private
   * @returns {string}
   * @memberof QuickCartViewComponent
   */
  private getSubDomain(): string {
    const siteDomain = toLower(this.document.location.host);
    const split = siteDomain.split(".");
    let subDomain = '';
    if (split.length == 1) {
      subDomain = split[0];
    } else if (split.length == 2) {
      subDomain = split.join('.');
    } else {
      let domain = '';
      split.forEach(function (val, key) {
        if (key != 0) {
          domain += val;
          if (key != split.length - 1) domain += '.';
        }
      });
      subDomain = domain;
    }
    return subDomain;
  }

  /**
   * Handle Checkout
   * @private
   * @memberof QuickCartViewComponent
   */
  private handleCheckout(jwt: string | undefined, email: string, items: any[], companyid: number | null): void {
    try {
      const customerType = this.layoutService.getCustomerType();
      const currentCountry = this.layoutService.getCurrentCountry();
      const currentLang = this.layoutService.getCustomerLanguage();
      const currency = this.layoutService.getCustomerCurrency();
      const languageId = this.commonService.getUserLocale(currentLang).id;
      const lang = this.translocoService.getActiveLang();
      const postIds = items.map((i: any) => i.id);
      const cookieData: any = {
        'migrate-cart-post_id': postIds,
        'migrate-cart-customer_type': Number(customerType),
        'migrate-cart-country_id': String(currentCountry.id),
        'migrate-cart-language_id': String(languageId),
        'migrate-cart-currency': currency
      };
      if (jwt) cookieData['user-token'] = jwt;
      const data = JSON.stringify(cookieData);
      const domain = this.getSubDomain();
      const cookieExpiryDate = new Date();
      cookieExpiryDate.setMonth(cookieExpiryDate.getMonth() + 1);
      this.cookieService.set('perfectglobalshareddata', data, { domain, secure: true, path: '/', sameSite: 'Lax', expires: cookieExpiryDate });
      if (environment.googleAnalytics.isEnabled && isPlatformBrowser(this.platformId) && items.length > 0) this.sendViewCartAndBeginCheckoutEvent('begin_checkout', items);
      const masterParam = companyid != null ? `&master=${companyid}` : '';
      this.document.location.assign(`${this.checkoutUrl}/${lang}/migrate/redirect-to-cart?email=${email}${masterParam}`);
      this.activeOffcanvas.close();
    } catch (error) {
      this.loadingOnNavigateToCheckout = false;
      this.snackBar.open(this.translocoService.translate('sentence_something_went_wrong'), this.translocoService.translate('label_close'), {
        panelClass: ['danger-dialog']
      });
      this.globalErrorHandler.handleError(`quick-cart-handle-checkout`, error)
    }
  }

  /**
  * Send Google Analytics Event - view_cart & begin_checkout
  * @private
  * @returns {void}
  * @memberof QuickCartViewComponent
  */
  private sendViewCartAndBeginCheckoutEvent(event_name: string, products: any[]): void {
    try {
      if (!window.dataLayer) return;
      const items = map(products, (item: any) => item?.context?.analytics);
      const totalPrice = items.reduce((accumulator, currentItem) => accumulator + currentItem.price, 0);
      gtag('event', event_name, {
        currency: get(environment, 'googleAnalytics.currency'),
        value: totalPrice,
        items
      });
    } catch (error) {
      console.log('view-cart-and-begin-checkout-ga4', error)
    }
  }

  /**
   * Send Google Analytics Event - remove_from_cart
   * @private
   * @returns {void}
   * @memberof QuickCartViewComponent
   */
  private sendDeleteGAEvent(analytics: any): void {
    try {
      if (!window.dataLayer) return;
      gtag('event', 'remove_from_cart', {
        currency: get(environment, 'googleAnalytics.currency'),
        value: get(analytics, 'price', 0),
        items: [analytics],
      });
    } catch (error) {
      console.log('remove-from-cart-ga4', error)
    }
  }

  /**
   * Check for part if available or not
   *  If not exists then update Local cart storage
   * @private
   * @param {*} response
   * @memberof QuickCartViewComponent
   */
  private handleCartProducts(response: any): void {
    try {
      const products = get(response, 'data.result', []);
      const postIds: Set<number> = new Set(map(products, (i) => i.id));
      this.isCartEmpty.set(get(response, 'total', 0) === 0);
      this.cartService.updateCartParts(postIds);
    } catch (error) {
      this.globalErrorHandler.handleError(`quick-cart-handle-cart-result`, error)
    }
  }
}
